import React, { FC, useMemo } from 'react';

import { Nullable } from 'src/types/nullable.type';

import { DataTable } from 'src/common/components/table/data-table';
import { generateColumn } from 'src/common/components/table/LegacyTable';
import { ContentInsight, JourneyInsight } from '../content-insight.interface';
import { getTotalViews, getAverageViewTime } from './metrics';
import { Row } from 'react-table';
import { useCurrentOrganization } from 'src/store/organization';
import { useHistory } from 'react-router-dom';
import { TableHeaderWithTooltip } from '../table-header-with-tooltip';
import { formatNumber } from 'src/utils/text';

interface DashboardInsightsContentJourneysTableProps {
  contentInsight?: Nullable<ContentInsight>;
}

export const DashboardInsightsContentJourneysTable: FC<DashboardInsightsContentJourneysTableProps> = ({
  contentInsight,
}) => {
  const history = useHistory();
  const { currentOrganization } = useCurrentOrganization();

  const columns = useMemo(
    () => [
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Name of Journey using this content'>Journey</TableHeaderWithTooltip>,
        accessor: 'journey_name',
        width: '40%',
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => (
          <span className='line-clamp-2 lg:line-clamp-none'>{row.original.journey.name}</span>
        ),
      }),
      generateColumn({
        Header: (
          <TableHeaderWithTooltip tooltip='Total times this content was viewed within this Journey'>
            Total views
          </TableHeaderWithTooltip>
        ),
        accessor: 'views',
        width: 100,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => formatNumber(getTotalViews(row.original)),
      }),
      generateColumn({
        Header: (
          <TableHeaderWithTooltip tooltip='The step name this content was used within'>Step</TableHeaderWithTooltip>
        ),
        accessor: 'node_name',
        width: '30%',
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => (
          <span className='line-clamp-3 lg:line-clamp-none'>{row.original.node.name}</span>
        ),
      }),
      generateColumn({
        Header: (
          <TableHeaderWithTooltip tooltip='Average time spent on this step within this Journey'>
            Average view time
          </TableHeaderWithTooltip>
        ),
        accessor: 'time',
        width: 200,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => getAverageViewTime(row.original),
      }),
    ],
    []
  );

  const onRowClick = (row: Row) => {
    const { journey } = row.original as JourneyInsight;
    history.push(`/${currentOrganization.slug}/insights/journeys/${journey.uuid}`);
  };

  return (
    <div className='flex flex-col space-y-4 w-full'>
      <div className='heading-bedrock-tertiary text-bedrock-black'>Journeys using this content</div>
      {contentInsight && (
        <DataTable tableClassName='w-full' columns={columns} data={contentInsight.journeys} onRowClick={onRowClick} />
      )}
    </div>
  );
};
